import { owned, ownerOf } from "./handles.js";
const ID_PATTERN = /^[a-z][a-z0-9-]*$/;
export function defineGame(input) {
    if (!ID_PATTERN.test(input.id)) {
        throw new TypeError(`Invalid game id: ${input.id}`);
    }
    if (!Number.isSafeInteger(input.simulationVersion) || input.simulationVersion < 1) {
        throw new TypeError("simulationVersion must be a positive safe integer");
    }
    if (!Number.isSafeInteger(input.stepMs) || input.stepMs < 1) {
        throw new TypeError("stepMs must be a positive safe integer");
    }
    return Object.freeze({
        id: input.id,
        simulationVersion: input.simulationVersion,
        stepMs: input.stepMs,
    });
}
export function defineOwnedGame(input, owner) {
    const base = defineGame(input);
    const seen = new Set();
    for (const resource of input.resources) {
        if (ownerOf(resource) !== owner)
            throw new TypeError(`Resource ${resource.id} belongs to another game kit`);
        if (seen.has(resource.id))
            throw new TypeError(`Duplicate resource id: ${resource.id}`);
        seen.add(resource.id);
    }
    const flowIds = new Set();
    for (const flow of input.flows ?? []) {
        if (ownerOf(flow) !== owner)
            throw new TypeError(`Flow ${flow.id} belongs to another game kit`);
        if (flowIds.has(flow.id))
            throw new TypeError(`Duplicate flow id: ${flow.id}`);
        flowIds.add(flow.id);
    }
    const buyableIds = new Set();
    for (const buyable of input.buyables ?? []) {
        if (ownerOf(buyable) !== owner)
            throw new TypeError(`Buyable ${buyable.id} belongs to another game kit`);
        if (buyableIds.has(buyable.id))
            throw new TypeError(`Duplicate buyable id: ${buyable.id}`);
        buyableIds.add(buyable.id);
    }
    validateOwnedIds(input.recipes ?? [], owner, "Recipe");
    validateOwnedIds(input.allocations ?? [], owner, "Allocation");
    validateOwnedIds(input.prestiges ?? [], owner, "Prestige");
    validateOwnedIds(input.upgrades ?? [], owner, "Upgrade");
    validateOwnedIds(input.triggers ?? [], owner, "Trigger");
    validateOwnedIds(input.challenges ?? [], owner, "Challenge");
    validateOwnedIds(input.automation ?? [], owner, "Automation");
    validateOwnedIds(input.scopeActivations ?? [], owner, "Scope activation");
    validateOwnedIds(input.steppedRules ?? [], owner, "Stepped rule");
    validateOwnedIds(input.tasks ?? [], owner, "Task");
    validateOwnedIds(input.calendars ?? [], owner, "Calendar");
    validateOwnedIds(input.markets ?? [], owner, "Market");
    validateOwnedIds(input.records ?? [], owner, "Record");
    validateOwnedIds(input.domainEvents ?? [], owner, "Domain event");
    const eventRetention = input.eventRetention ?? 2_000;
    if (!Number.isSafeInteger(eventRetention) || eventRetention < 1) {
        throw new TypeError("Event retention must be a positive safe integer");
    }
    for (const calendar of input.calendars ?? []) {
        if (calendar.phases.some((phase) => phase.durationMs % base.stepMs !== 0))
            throw new TypeError(`Calendar ${calendar.id} phase durations must align to stepMs`);
    }
    return owned({
        id: base.id,
        simulationVersion: base.simulationVersion,
        stepMs: base.stepMs,
        rootSeed: input.rootSeed ?? "00",
        numbers: input.numbers,
        resources: Object.freeze([...input.resources]),
        flows: Object.freeze([...(input.flows ?? [])]),
        buyables: Object.freeze([...(input.buyables ?? [])]),
        recipes: Object.freeze([...(input.recipes ?? [])]),
        allocations: Object.freeze([...(input.allocations ?? [])]),
        prestiges: Object.freeze([...(input.prestiges ?? [])]),
        upgrades: Object.freeze([...(input.upgrades ?? [])]),
        triggers: Object.freeze([...(input.triggers ?? [])]),
        challenges: Object.freeze([...(input.challenges ?? [])]),
        automation: Object.freeze([...(input.automation ?? [])]),
        scopeActivations: Object.freeze([...(input.scopeActivations ?? [])]),
        ...(input.win === undefined ? {} : { win: input.win }),
        steppedRules: Object.freeze([...(input.steppedRules ?? [])]),
        tasks: Object.freeze([...(input.tasks ?? [])]),
        calendars: Object.freeze([...(input.calendars ?? [])]),
        markets: Object.freeze([...(input.markets ?? [])]),
        records: Object.freeze([...(input.records ?? [])]),
        domainEvents: Object.freeze([...(input.domainEvents ?? [])]),
        eventRetention,
    }, owner);
}
function validateOwnedIds(values, owner, kind) {
    const ids = new Set();
    for (const value of values) {
        if (ownerOf(value) !== owner)
            throw new TypeError(`${kind} ${value.id} belongs to another game kit`);
        if (ids.has(value.id))
            throw new TypeError(`Duplicate ${kind.toLowerCase()} id: ${value.id}`);
        ids.add(value.id);
    }
}
export function definitionOwner(definition) {
    const owner = ownerOf(definition);
    if (!owner)
        throw new TypeError("Game definition has no owner");
    return owner;
}
export function definitionScopes(definition) {
    const groups = [
        definition.resources ?? [],
        definition.flows ?? [],
        definition.buyables ?? [],
        definition.recipes ?? [],
        definition.allocations ?? [],
        definition.prestiges ?? [],
        definition.upgrades ?? [],
        definition.triggers ?? [],
        definition.challenges ?? [],
        definition.automation ?? [],
        definition.scopeActivations ?? [],
        definition.steppedRules ?? [],
        definition.tasks ?? [],
        definition.calendars ?? [],
        definition.markets ?? [],
        definition.records ?? [],
    ];
    const scopes = new Map();
    for (const group of groups) {
        for (const value of group)
            scopes.set(value.scope.id, value.scope);
    }
    return Object.freeze([...scopes.values()].sort((left, right) => left.id < right.id ? -1 : left.id > right.id ? 1 : 0));
}
//# sourceMappingURL=definition.js.map